import { CycleUtils } from '@wix/package-picker-shared';
import React from 'react';
import { useCurrentModule } from '../components/ModuleProvider';
import { MOBILE_STICKY_BUTTON_SPEC } from '../utils/consts';
import { availableCyclesFromPricingDetails, clampToAvailableCycle, productFamilyFromId, recommendedCycleToPurchaseCycle } from './cycleRecommendation.utils';
import { resolveCurrentDefaultCycle } from './resolveCurrentDefaultCycle';
import { useCycleAvailability } from './useCycleAvailability';
import { useEclipseCycleRecommendation } from './useEclipseCycleRecommendation';
export const useCycleDefaultDecision = () => {
  const module = useCurrentModule();
  const selectedProductId = module.SDK.PackagePickerStore.useSelectedProductId();
  const {
    data: discountData,
    isLoading: isDiscountLoading
  } = module.SDK.eclipseOfferingApiClient.hooks.useFetchOfferingDiscount({});
  const {
    data: product,
    isLoading: isProductLoading
  } = module.SDK.eclipseOfferingApiClient.hooks.useFetchProduct({
    productId: selectedProductId ?? ''
  }, Boolean(selectedProductId));
  const {
    cycleByFamily,
    correlationId,
    isEligible,
    ineligibleReasons,
    isError,
    isTimeout,
    isLoading: isRecommendationLoading
  } = useEclipseCycleRecommendation();
  const {
    experiments
  } = module.internals.experiments.hooks.useExperiments();
  const isMobile = module.moduleName === 'eclipse-package-picker-mobile-module';
  const isStickyButtonYearly = isMobile && experiments.isEnabled(MOBILE_STICKY_BUTTON_SPEC);
  const isProductPending = Boolean(selectedProductId) && isProductLoading;
  const productFamily = productFamilyFromId(product == null ? void 0 : product.productFamilyId);
  const recommendedCycle = isEligible ? productFamily && cycleByFamily[productFamily] : undefined;
  const recommendedPurchaseCycle = recommendedCycleToPurchaseCycle(recommendedCycle);
  const availableCycles = availableCyclesFromPricingDetails(product == null ? void 0 : product.pricingDetails);
  const currentDefaultCycle = resolveCurrentDefaultCycle({
    hasSale: discountData == null ? void 0 : discountData.hasSale,
    alwaysDefaultsToYearly: isStickyButtonYearly && !isEligible
  });
  const presetCycle = recommendedPurchaseCycle ? clampToAvailableCycle(recommendedPurchaseCycle, availableCycles) : currentDefaultCycle;
  const {
    lowestCycleAvailable,
    isLoading: isCycleAvailabilityLoading
  } = useCycleAvailability(selectedProductId || undefined);
  const isLoading = isDiscountLoading || isRecommendationLoading || isCycleAvailabilityLoading || isProductPending;
  const servedCycle = CycleUtils.maxCycle([lowestCycleAvailable, presetCycle]) || presetCycle;
  const fallbackReasons = React.useMemo(() => {
    if (!isEligible || recommendedPurchaseCycle) {
      return [];
    }
    const reasons = [];
    if (isTimeout) {
      reasons.push('resolver_timeout');
    } else if (isError) {
      reasons.push('resolver_error');
    }
    if (!isError) {
      if (!correlationId) {
        reasons.push('no_response_data');
      } else {
        reasons.push(productFamily ? 'suppressed_product' : 'unknown_product_family');
      }
    }
    return reasons;
  }, [isEligible, recommendedPurchaseCycle, isTimeout, isError, correlationId, productFamily]);
  return {
    servedCycle,
    recommendedCycle: recommendedCycle || undefined,
    productFamily,
    availableCycles,
    applied: isEligible && Boolean(recommendedPurchaseCycle),
    fallbackReasons,
    ineligibleReasons,
    correlationId,
    isEligible,
    isLoading
  };
};
//# sourceMappingURL=useCycleDefaultDecision.js.map